import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Calendar, Eye, EyeOff, Archive, ArrowUp, ArrowDown } from "lucide-react";

const STATUS_LABEL = { draft: "Draft", published: "Published", archived: "Archived" };

function emptyEvent() {
  return {
    slug: "",
    title: { hr: "", en: "" },
    description_html: { hr: "", en: "" },
    starts_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000).toISOString(),
    graphic_url: "",
    booking_mode: "table_only",
    ticket_price: 0,
    highlighted_products: [],
    beverage_packages: [],
    status: "draft",
    featured: false,
    pricing_override: { enabled: false, mode: "reservation_only", flat_fee_amount: 0, min_consumption_per_person: 0, deposit_required: false, pay_now_enabled: false, reservation_only_enabled: true, allow_user_choice: false },
  };
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}
function fromLocalInput(s) {
  if (!s) return null;
  return new Date(s).toISOString();
}

function EventEditor({ value, open, onOpenChange, onSave }) {
  const [ev, setEv] = useState(value || emptyEvent());
  useEffect(() => setEv(value || emptyEvent()), [value]);
  const set = (k, v) => setEv((cur) => ({ ...cur, [k]: v }));
  const setLoc = (k, lng, v) => setEv((cur) => ({ ...cur, [k]: { ...cur[k], [lng]: v } }));
  const setPo = (k, v) => setEv((cur) => ({ ...cur, pricing_override: { ...cur.pricing_override, [k]: v } }));

  const updateList = (key, idx, patch) => setEv((cur) => {
    const next = [...(cur[key] || [])];
    next[idx] = { ...next[idx], ...patch };
    return { ...cur, [key]: next };
  });
  const updateListNested = (key, idx, field, lng, val) => setEv((cur) => {
    const next = [...(cur[key] || [])];
    next[idx] = { ...next[idx], [field]: { ...(next[idx][field] || {}), [lng]: val } };
    return { ...cur, [key]: next };
  });
  const removeRow = (key, idx) => setEv((cur) => ({ ...cur, [key]: cur[key].filter((_, i) => i !== idx) }));
  const addRow = (key, blank) => setEv((cur) => ({ ...cur, [key]: [...(cur[key] || []), blank] }));
  const moveRow = (key, idx, dir) => setEv((cur) => {
    const next = [...(cur[key] || [])];
    const j = idx + dir; if (j < 0 || j >= next.length) return cur;
    [next[idx], next[j]] = [next[j], next[idx]];
    return { ...cur, [key]: next };
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="event-editor-dialog">
        <DialogHeader>
          <DialogTitle>{ev.id ? "Edit event" : "New event"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input value={ev.slug} onChange={(e) => set("slug", e.target.value)} data-testid="event-slug" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={ev.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="event-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Starts (Europe/Zagreb)</Label>
              <Input type="datetime-local" value={toLocalInput(ev.starts_at)} onChange={(e) => set("starts_at", fromLocalInput(e.target.value))} data-testid="event-starts-at" />
            </div>
            <div className="space-y-2">
              <Label>Ends</Label>
              <Input type="datetime-local" value={toLocalInput(ev.ends_at)} onChange={(e) => set("ends_at", fromLocalInput(e.target.value))} data-testid="event-ends-at" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Graphic URL</Label>
              <Input value={ev.graphic_url || ""} onChange={(e) => set("graphic_url", e.target.value)} placeholder="https://…" />
            </div>
            <div className="space-y-2">
              <Label>Booking mode</Label>
              <Select value={ev.booking_mode} onValueChange={(v) => set("booking_mode", v)}>
                <SelectTrigger data-testid="event-booking-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="table_only">Table only</SelectItem>
                  <SelectItem value="ticket_and_table">Ticket + table</SelectItem>
                  <SelectItem value="no_booking">No booking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ev.booking_mode === "ticket_and_table" && (
              <div className="space-y-2">
                <Label>Ticket price (per person)</Label>
                <Input type="number" step="0.5" value={ev.ticket_price} onChange={(e) => set("ticket_price", parseFloat(e.target.value || "0"))} />
              </div>
            )}
          </div>

          <Tabs defaultValue="hr">
            <TabsList data-testid="event-locale-tabs">
              <TabsTrigger value="hr">Hrvatski</TabsTrigger>
              <TabsTrigger value="en">English</TabsTrigger>
            </TabsList>
            {["hr", "en"].map((lng) => (
              <TabsContent value={lng} key={lng} className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label>Title ({lng.toUpperCase()})</Label>
                  <Input value={ev.title?.[lng] || ""} onChange={(e) => setLoc("title", lng, e.target.value)} data-testid={`event-title-${lng}`} />
                </div>
                <div className="space-y-2">
                  <Label>Description ({lng.toUpperCase()})</Label>
                  <RichTextEditor value={ev.description_html?.[lng] || ""} onChange={(html) => setLoc("description_html", lng, html)} testid={`event-rte-${lng}`} />
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <section className="border border-border rounded-lg p-4 space-y-3" data-testid="event-products-editor">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg">Highlighted products</h3>
              <Button size="sm" variant="outline" className="border-border" onClick={() => addRow("highlighted_products", { name: { hr: "", en: "" }, price: 0, image_url: null, sort_order: (ev.highlighted_products || []).length })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {(ev.highlighted_products || []).map((p, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_100px_auto] gap-2 items-end" data-testid={`event-product-row-${idx}`}>
                <Input value={p.name?.hr || ""} onChange={(e) => updateListNested("highlighted_products", idx, "name", "hr", e.target.value)} placeholder="HR name" />
                <Input value={p.name?.en || ""} onChange={(e) => updateListNested("highlighted_products", idx, "name", "en", e.target.value)} placeholder="EN name" />
                <Input type="number" step="0.5" value={p.price} onChange={(e) => updateList("highlighted_products", idx, { price: parseFloat(e.target.value || "0") })} />
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveRow("highlighted_products", idx, -1)} className="p-1 text-muted-foreground hover:text-foreground"><ArrowUp className="w-3 h-3" /></button>
                  <button type="button" onClick={() => moveRow("highlighted_products", idx, 1)} className="p-1 text-muted-foreground hover:text-foreground"><ArrowDown className="w-3 h-3" /></button>
                  <button type="button" onClick={() => removeRow("highlighted_products", idx)} className="p-1 text-destructive"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </section>

          <section className="border border-border rounded-lg p-4 space-y-3" data-testid="event-packages-editor">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg">Beverage packages</h3>
              <Button size="sm" variant="outline" className="border-border" onClick={() => addRow("beverage_packages", { title: { hr: "", en: "" }, description: { hr: "", en: "" }, price: 0, image_url: null, sort_order: (ev.beverage_packages || []).length })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {(ev.beverage_packages || []).map((p, idx) => (
              <div key={idx} className="border border-border rounded-md p-2 space-y-2" data-testid={`event-package-row-${idx}`}>
                <div className="grid grid-cols-[1fr_1fr_100px_auto] gap-2 items-end">
                  <Input value={p.title?.hr || ""} onChange={(e) => updateListNested("beverage_packages", idx, "title", "hr", e.target.value)} placeholder="Title HR" />
                  <Input value={p.title?.en || ""} onChange={(e) => updateListNested("beverage_packages", idx, "title", "en", e.target.value)} placeholder="Title EN" />
                  <Input type="number" step="0.5" value={p.price} onChange={(e) => updateList("beverage_packages", idx, { price: parseFloat(e.target.value || "0") })} />
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveRow("beverage_packages", idx, -1)} className="p-1 text-muted-foreground hover:text-foreground"><ArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveRow("beverage_packages", idx, 1)} className="p-1 text-muted-foreground hover:text-foreground"><ArrowDown className="w-3 h-3" /></button>
                    <button type="button" onClick={() => removeRow("beverage_packages", idx)} className="p-1 text-destructive"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                <Textarea rows={2} value={p.description?.hr || ""} onChange={(e) => updateListNested("beverage_packages", idx, "description", "hr", e.target.value)} placeholder="Description HR" />
                <Textarea rows={2} value={p.description?.en || ""} onChange={(e) => updateListNested("beverage_packages", idx, "description", "en", e.target.value)} placeholder="Description EN" />
              </div>
            ))}
          </section>

          <section className="border border-border rounded-lg p-4 space-y-3" data-testid="event-pricing-override">
            <div className="flex items-center gap-2">
              <Checkbox id="po" checked={!!ev.pricing_override?.enabled} onCheckedChange={(v) => setPo("enabled", Boolean(v))} data-testid="event-po-enabled" />
              <Label htmlFor="po" className="font-display text-lg">Pricing override for this event</Label>
            </div>
            {ev.pricing_override?.enabled && (
              <div className="grid grid-cols-2 gap-3 pl-7">
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={ev.pricing_override.mode} onValueChange={(v) => setPo("mode", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reservation_only">Reservation only</SelectItem>
                      <SelectItem value="flat_fee">Flat fee</SelectItem>
                      <SelectItem value="min_consumption">Minimum consumption</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Flat fee</Label>
                  <Input type="number" step="0.5" value={ev.pricing_override.flat_fee_amount || 0} onChange={(e) => setPo("flat_fee_amount", parseFloat(e.target.value || "0"))} />
                </div>
                <div className="space-y-2">
                  <Label>Min consumption / person</Label>
                  <Input type="number" step="0.5" value={ev.pricing_override.min_consumption_per_person || 0} onChange={(e) => setPo("min_consumption_per_person", parseFloat(e.target.value || "0"))} />
                </div>
                <div className="space-y-2 flex flex-col justify-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox checked={!!ev.pricing_override.pay_now_enabled} onCheckedChange={(v) => setPo("pay_now_enabled", Boolean(v))} /> Pay-now
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox checked={!!ev.pricing_override.reservation_only_enabled} onCheckedChange={(v) => setPo("reservation_only_enabled", Boolean(v))} /> Reservation
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <Checkbox checked={!!ev.pricing_override.allow_user_choice} onCheckedChange={(v) => setPo("allow_user_choice", Boolean(v))} /> Allow user choice
                  </label>
                </div>
              </div>
            )}
          </section>

          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox checked={!!ev.featured} onCheckedChange={(v) => set("featured", Boolean(v))} data-testid="event-featured" />
            Featured on home / events list
          </label>
        </div>
        <DialogFooter>
          <Button onClick={() => onSave(ev)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="event-save-button">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("hr-HR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zagreb" }).format(new Date(iso));
  } catch { return iso; }
}

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/events");
      setItems(data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (ev) => {
    try {
      if (ev.id) {
        const payload = { ...ev };
        delete payload.id; delete payload.created_at; delete payload.updated_at;
        await api.patch(`/admin/events/${ev.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", ev);
        toast.success("Event created");
      }
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/events/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const setStatus = async (ev, status) => {
    try { await api.patch(`/admin/events/${ev.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-events-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Events</h1>
          </div>
          <Button onClick={() => setEditing(emptyEvent())} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="event-new-button">
            <Plus className="w-4 h-4 mr-1" /> Create event
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground">
            <Calendar className="w-8 h-8 mx-auto mb-3 opacity-60" /> No events yet.
          </div>
        ) : (
          <div className="border border-border rounded-xl bg-card divide-y divide-border" data-testid="admin-events-list">
            {items.map((ev) => (
              <div key={ev.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`event-row-${ev.id}`}>
                <span className={`w-2 h-2 rounded-full ${ev.status === "published" ? "bg-primary" : ev.status === "draft" ? "bg-amber-400" : "bg-muted-foreground/60"}`} />
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium">{ev.title?.hr || ev.title?.en}</div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(ev.starts_at)} · {ev.booking_mode}</div>
                </div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{STATUS_LABEL[ev.status]}</span>
                <div className="flex gap-1">
                  {ev.status !== "published" && (
                    <Button size="sm" variant="outline" className="border-border" onClick={() => setStatus(ev, "published")} data-testid={`event-publish-${ev.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                  )}
                  {ev.status === "published" && (
                    <Button size="sm" variant="outline" className="border-border" onClick={() => setStatus(ev, "draft")} data-testid={`event-unpublish-${ev.id}`}><EyeOff className="w-3.5 h-3.5" /></Button>
                  )}
                  {ev.status !== "archived" && (
                    <Button size="sm" variant="outline" className="border-border" onClick={() => setStatus(ev, "archived")} data-testid={`event-archive-${ev.id}`}><Archive className="w-3.5 h-3.5" /></Button>
                  )}
                  <Button size="sm" variant="outline" className="border-border" onClick={() => setEditing(ev)} data-testid={`event-edit-${ev.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-border text-destructive" data-testid={`event-delete-${ev.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete event?</AlertDialogTitle></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(ev.id)} className="bg-destructive text-destructive-foreground" data-testid={`event-delete-confirm-${ev.id}`}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}

        <EventEditor open={!!editing} onOpenChange={(v) => !v && setEditing(null)} value={editing} onSave={save} />
      </div>
    </AdminLayout>
  );
}
